import { DatePicker, Select, Table, TableColumnsType } from "antd";
import _ from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { getTextSummaries } from "../../../api/statistics.api";
import useAxios from "../../../hooks/useAxios";
import { IGetTextSummaries, ITextSummary } from "../../../models/statistics.model";
import { RootState } from "../../../store/rootReducer";
import Button from "../../Button";
import DownloadButton from "../../DownloadButton";
import ErrorResponse from "../../ErrorResponse";
import Loader from "../../Loader";
import ScrollView from "../../payment/ScrollView";
import SelectCampus from "../../SelectCampus";
import exportTitleTable from "../../../lib/exportTitleTable";
import TableEmpty from "../../common/TableEmpty";

const Summary = () => {
  const { t, i18n } = useTranslation();
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { data: res, isLoading, error, execute } = useAxios<IGetTextSummaries>();
  
  const [campusCode, setCampusCode] = useState<string>("");
  const [date, setDate] = useState(moment().format('YYYYMM'));
  const [data, setData] = useState<ITextSummary[]>([]);
  const [excelCampusName, setExcelCampusName] = useState<string>();

  useEffect(() => {
    if (res && !isLoading) {
      const _data: ITextSummary[] = _.cloneDeep(res.data);
      let tempExcelCompanyName = "";

      if (auth.hqYn === 'Y' && !campusCode) {
        const smsTotal = {
          campusCode: '',
          campusKorName: t('statistics.attendance.3'),
          campusEngName: 'ALL',
          smsType: 'SMS',
          totalCount: 0,
          successCount: 0,
          failCount: 0,
        }
        const lmsTotal = {
          ...smsTotal,
          smsType: 'LMS',
        }
        _data.forEach(item => {
          if (item.smsType === 'SMS') {
            smsTotal.totalCount += item.totalCount;
            smsTotal.successCount += item.successCount;
            smsTotal.failCount += item.failCount;
          } else {
            lmsTotal.totalCount += item.totalCount;
            lmsTotal.successCount += item.successCount;
            lmsTotal.failCount += item.failCount;
          }
        });
        _data.unshift(smsTotal);
        _data.unshift(lmsTotal);

        tempExcelCompanyName = t('common.text.all');
      } else {
        res.data.forEach(item => {
          if(!tempExcelCompanyName) {
            tempExcelCompanyName = item.campusKorName;
          }
        });
      }
      
      setExcelCampusName(tempExcelCompanyName);
      setData(_data);
    }
  }, [res, isLoading]);

  const onSearch = () => {
    const param: any = {
      year: Number(date.slice(0, 4)),
      month: Number(date.slice(4, 6)),
    };

    if (auth.hqYn === 'Y' && campusCode) {
      param.campusCode = campusCode;
    } else if (auth.hqYn === 'N' && currentCampus) {
      param.campusCode = currentCampus;
    }

    execute(getTextSummaries(param));
  }

  const onExportTable = () => {
    const currentDate = moment(new Date());

    let excelTitle = t('statistics.text.2');
    let arrSubTitle = [];

    let today = '';
    if(currentDate.format('YYYYMM') === date) {
      today = currentDate.format('DD');
    } else {
      today = moment(date).add(1, 'month').subtract(1, 'day').format('DD');
    }

    arrSubTitle.push(t('common.excel.campus') + " : " + excelCampusName);
    arrSubTitle.push(t('common.excel.period') + " : " + date.slice(0, 4) + date.slice(4, 6) + '01 ~ ' + date.slice(0, 4) + date.slice(4, 6) + today);

    exportTitleTable(columns, data, `${t('statistics.text.2')}_(${currentDate.format('YYYYMMDD')})`, t('statistics.regularTest.6'), [15, 12, 12, 12, 12], excelTitle, arrSubTitle);
  }

  const columns: TableColumnsType<ITextSummary> = [
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusKorName',
      key: 'campusKorName',
      align: 'center',
    },
    {
      title: t('statistics.text.column1'),
      dataIndex: 'smsType',
      key: 'smsType',
      align: 'center',
    },
    {
      title: t('statistics.text.column2'),
      dataIndex: 'totalCount',
      key: 'totalCount',
      align: 'center',
    },
    {
      title: t('sms.list.column7'),
      dataIndex: 'successCount',
      key: 'successCount',
      align: 'center',
    },
    {
      title: t('sms.list.column8'),
      dataIndex: 'failCount',
      key: 'failCount',
      align: 'center',
    },
  ];

  return (
    <>
      <div className="common-search">
        <div className="search-area">
          <ul className="search-bx">
            <li>
              <label>{t('common.menu.campus')}</label> {/* 캠퍼스 */}
              {auth.hqYn === 'Y' ? (
                <SelectCampus
                  placeholder={t('statistics.regularTest.placeholder2')}
                  value={campusCode}
                  onChange={(value: string) => setCampusCode(value)}
                  hasAll={true}
                />
              ) : (
                <SelectCampus />
              )}
            </li>
            <li>
              <label>{t('sms.list.column1')}</label> {/* 기간 */}
              <div className="date-picker__area">
                <DatePicker
                  value={date ? moment(date) : undefined}
                  onChange={e => {
                    if (e === null) setDate('');
                    else setDate(moment(e).format('YYYYMM'))
                  }}
                  picker="month"
                />
              </div>
              <div className="comm-btn__area">
                <Button
                  className="btn-basic small black"
                  onClick={onSearch}
                  text={t('common.button.search')}
                />
              </div>
            </li>
          </ul>
        </div>
      </div>	

      {isLoading && <Loader />}
      <ErrorResponse error={error} />
      {
        res?.data?.length
          ? (
            <>
              <div className="comm-table__area">
                <Table
                  columns={columns}
                  dataSource={data}
                  pagination={{
                    pageSize: 999999999,
                  }}
                  rowClassName={(record: ITextSummary) => record.campusCode === '' ? 'tfoot' : ''}
                />
              </div>
              <div className="comm-bx__space">
                <DownloadButton onClick={onExportTable} />
              </div>
            </>
            )
          : isLoading
            ? null
            : (
              <TableEmpty text={t('statistics.appday.text1')} />
              )
      }
    </>
  );
}

export default Summary;
